import React, { useEffect, useState, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Clock, CheckCircle2, ShieldCheck, ArrowRight, Star, BookOpen, Calendar, Utensils, PlayCircle, ShoppingCart, ChevronDown, X } from 'lucide-react';
// --- Components ---
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState(14 * 60 + 59); // 14:59 in seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  return <span className="font-mono font-bold text-base md:text-lg tracking-wider bg-white/30 px-3 py-1.5 rounded-md shadow-lg border border-white/40" data-id="element-0">
      {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
    </span>;
};
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.7,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-1">
    {children}
  </motion.div>;
const CTAButton = ({
  children,
  href = '#offer',
  onClick
}: {
  children: React.ReactNode;
  href?: string;
  onClick?: () => void;
}) => <motion.a href={href} onClick={onClick} whileHover={{
  scale: 1.02
}} whileTap={{
  scale: 0.98
}} className="inline-flex items-center justify-center w-full md:w-auto px-8 py-5 text-lg md:text-xl font-bold text-white bg-brand-burgundy rounded-full shadow-glow hover:bg-brand-burgundyDark transition-colors duration-300 group" data-id="element-2">
    {children}
    <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-3" />
  </motion.a>;
// --- Main Page Component ---
export function App() {
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  // Meta Pixel - PageView tracking
  useEffect(() => {
    // Initialize Meta Pixel
    if (typeof window !== 'undefined' && !(window as any).fbq) {
      ;
      (function (f: any, b: any, e: string, v: string) {
        if (f.fbq) return;
        const n: any = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        const t = b.createElement(e);
        t.async = true;
        t.src = v;
        const s = b.getElementsByTagName(e)[0];
        if (s && s.parentNode) {
          s.parentNode.insertBefore(t, s);
        } else {
          b.head.appendChild(t);
        }
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      (window as any).fbq('init', '2134630104036378');
    }
    // Track PageView
    if ((window as any).fbq) {
      ;
      (window as any).fbq('track', 'PageView');
    }
  }, []);
  // Function to track AddToCart
  const handleAddToCart = () => {
    if ((window as any).fbq) {
      ;
      (window as any).fbq('track', 'AddToCart', {
        content_name: 'Metabolic Reset 40+ Bundle',
        content_ids: ['51866486538522'],
        content_type: 'product',
        value: 12.37,
        currency: 'USD'
      });
    }
  };
  return <div className="min-h-screen bg-brand-cream font-sans text-brand-dark selection:bg-brand-goldLight selection:text-brand-burgundy" data-id="element-4">
      {/* 1. Sticky Urgency Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-red-500/90 text-white py-3 px-4 shadow-md animate-pulse" data-id="element-5">
        <div className="max-w-3xl mx-auto text-center" data-id="element-6">
          <div className="flex items-center justify-center gap-2 text-sm md:text-base font-medium" data-id="element-7">
            <span className="animate-pulse text-lg" data-id="element-8">⚠️⚡️</span>
            <span data-id="element-9">
              ¡SOLO HOY! Obtén el "Metabolic Reset 40+ Bundle" y ahorra $162 —
              Precio especial para nuevas lectoras que se sumen al método
            </span>
            <span className="animate-pulse text-lg" data-id="element-10">⚡️⚠️</span>
          </div>
        </div>
      </div>

      {/* Main Content Wrapper (offset for sticky header) */}
      <main className="pt-24 md:pt-20" data-id="element-11">
        {/* 2. Hero Section */}
        <section className="relative px-4 pt-12 pb-20 md:pt-20 md:pb-32 overflow-hidden" data-id="element-12">
          {/* Background decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10" data-id="element-13">
            <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-brand-goldLight/40 blur-3xl" data-id="element-14"></div>
            <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-brand-burgundy/5 blur-3xl" data-id="element-15"></div>
          </div>

          <div className="max-w-4xl mx-auto text-center" data-id="element-16">
            <FadeIn data-id="element-17">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-brand-burgundy leading-tight mb-6" data-id="element-18">
                Cómo recuperar tu cintura, tu energía y tu confianza en solo 30
                días...
              </h1>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-19">
              <p className="text-xl md:text-2xl font-serif italic text-brand-gray mb-8" data-id="element-20">
                ...sin pasar hambre, sin cardio infinito y sin pelear contra tus
                hormonas. Incluso si sientes que tu metabolismo "se detuvo" por
                completo después de cumplir los 40.
              </p>
            </FadeIn>

            <FadeIn delay={0.4} data-id="element-21">
              <div className="bg-white/60 backdrop-blur-sm border border-brand-gold/20 rounded-2xl p-6 md:p-8 shadow-soft max-w-3xl mx-auto mb-10" data-id="element-22">
                <p className="text-lg md:text-xl text-brand-dark leading-relaxed" data-id="element-23">
                  Deja de castigar a tu cuerpo con dietas de 1,200 calorías que
                  solo te inflaman. Descubre el{' '}
                  <strong className="text-brand-burgundy font-semibold" data-id="element-24">
                    Código Metabólico
                  </strong>{' '}
                  diseñado específicamente para la biología de la mujer moderna
                  de +40.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.6} data-id="element-25">
              <CTAButton href="#offer-intro" data-id="element-26">
                SÍ, QUIERO RECUPERAR MI ENERGÍA
              </CTAButton>
            </FadeIn>
          </div>
        </section>

        {/* 3. Hook Section */}
        <section className="px-4 py-20 bg-white" data-id="element-27">
          <div className="max-w-3xl mx-auto" data-id="element-28">
            <FadeIn data-id="element-29">
              <div className="flex items-center justify-center mb-8" data-id="element-30">
                <div className="w-16 h-px bg-brand-gold" data-id="element-31"></div>
                <Clock className="w-8 h-8 text-brand-gold mx-4" data-id="element-32" />
                <div className="w-16 h-px bg-brand-gold" data-id="element-33"></div>
              </div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-center mb-8" data-id="element-34">
                ¿Te ha pasado que te despiertas a las 3 de la mañana...
              </h2>
              <div className="space-y-6 text-lg text-brand-gray leading-relaxed" data-id="element-35">
                <p data-id="element-36">
                  ...con la mente a mil, sintiéndote hinchada y agotada, solo
                  para darte cuenta de que tus jeans favoritos ya no cierran...
                  a pesar de que "comes sano"?
                </p>
                <p className="font-medium text-brand-dark text-xl border-l-4 border-brand-burgundy pl-6 py-2" data-id="element-37">
                  No es tu imaginación. Y no, no es que te estés haciendo vieja.
                </p>
                <p data-id="element-38">
                  Es que las reglas de tu cuerpo acaban de cambiar y nadie te
                  dio el nuevo manual de instrucciones. Hasta hoy.
                </p>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* 4. Pain Agitation */}
        <section className="px-4 py-20 bg-brand-burgundy text-white relative overflow-hidden" data-id="element-39">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent" data-id="element-40"></div>
          <div className="max-w-3xl mx-auto relative z-10" data-id="element-41">
            <FadeIn data-id="element-42">
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-8 text-brand-goldLight" data-id="element-43">
                Es frustrante, lo sé.
              </h2>
              <div className="space-y-6 text-lg md:text-xl text-white/90 leading-relaxed font-light" data-id="element-44">
                <p data-id="element-45">
                  Pasas el día a base de café y ensaladas, te matas en la
                  elíptica y, aun así, la balanza no se mueve... o peor, sube.
                  Te sientes "inflada" permanentemente, como si tu cuerpo
                  estuviera reteniendo todo por miedo.
                </p>
                <div className="bg-black/20 p-6 md:p-8 rounded-xl border border-white/10 my-8" data-id="element-46">
                  <p className="font-medium" data-id="element-47">
                    Esa grasa abdominal rebelde no es falta de voluntad; es un
                    grito de auxilio de tus hormonas.
                  </p>
                </div>
                <p data-id="element-48">
                  El estrés de las dietas restrictivas está elevando tu
                  cortisol, bloqueando tu tiroides y diciéndole a tu cuerpo:{' '}
                  <em className="text-brand-goldLight font-serif" data-id="element-49">
                    "Guarda cada caloría porque estamos en peligro"
                  </em>
                  .
                </p>
                <p data-id="element-50">
                  Estás atrapada en un ciclo de inflamación que ninguna dieta
                  tradicional de "comer menos" puede solucionar. Si no cambias
                  el enfoque hoy, el cansancio y la frustración solo seguirán
                  creciendo.
                </p>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* 5. Revelation */}
        <section className="px-4 py-24 bg-brand-cream" data-id="element-51">
          <div className="max-w-4xl mx-auto" data-id="element-52">
            <FadeIn data-id="element-53">
              <div className="text-center mb-16" data-id="element-54">
                <span className="text-sm font-bold tracking-widest text-brand-gold uppercase mb-4 block" data-id="element-55">
                  El Nuevo Paradigma
                </span>
                <h2 className="text-3xl md:text-5xl font-serif font-bold text-brand-dark mb-6" data-id="element-56">
                  La verdad que la industria del fitness de "20 añeros" no
                  quiere que sepas:
                </h2>
                <p className="text-2xl font-serif italic text-brand-burgundy" data-id="element-57">
                  Tu cuerpo de +40 no necesita menos comida, necesita más
                  seguridad.
                </p>
              </div>
            </FadeIn>

            <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-58">
              <FadeIn delay={0.2} data-id="element-59">
                <div className="space-y-6 text-lg text-brand-gray" data-id="element-60">
                  <p data-id="element-61">
                    He descubierto que la clave no es el déficit calórico, sino
                    la{' '}
                    <strong className="text-brand-dark" data-id="element-62">
                      Seguridad Metabólica
                    </strong>
                    .
                  </p>
                  <p data-id="element-63">
                    Cuando dejas de estresar a tu sistema con cardio excesivo y
                    empiezas a darle señales de abundancia con Proteína de
                    Saciedad y Fuerza Minimalista, tu metabolismo se "relaja".
                  </p>
                  <div className="bg-white p-6 rounded-xl shadow-soft border border-brand-gold/20" data-id="element-64">
                    <p className="font-medium text-brand-dark" data-id="element-65">
                      En ese momento, y solo en ese momento, tu cuerpo libera la
                      grasa acumulada porque ya no siente que necesita
                      protegerse.
                    </p>
                  </div>
                  <p className="italic" data-id="element-66">
                    Es como encontrar la llave maestra de una cerradura que
                    estuvo oxidada por años.
                  </p>
                </div>
              </FadeIn>
              <FadeIn delay={0.4} className="relative" data-id="element-67">
                <div className="aspect-square rounded-full bg-brand-goldLight/30 absolute -inset-4 blur-2xl -z-10" data-id="element-68"></div>
                <img src="https://cdn.magicpatterns.com/uploads/82ic4Uu3HvT5jGjwwggAbZ/Mocke_up.png" alt="Metabolic Reset 40+ Bundle en dispositivos" className="w-full h-auto rounded-2xl shadow-2xl transform rotate-[-2deg] hover:rotate-0 transition-transform duration-500" data-id="element-69" />
              </FadeIn>
            </div>
          </div>
        </section>

        {/* 6. Social Proof */}
        <section className="px-4 py-24 bg-white" data-id="element-70">
          <div className="max-w-6xl mx-auto" data-id="element-71">
            <FadeIn data-id="element-72">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-center mb-16" data-id="element-73">
                Mujeres reales recuperando su vitalidad
              </h2>
            </FadeIn>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-74">
              {[{
              name: 'Sarah M.',
              location: 'Miami, FL',
              quote: 'A los 46 pensé que mi cuerpo atlético se había ido para siempre. Estaba siempre hinchada y sin ganas de nada. Con el Metabolic Reset, perdí 4 kilos de inflamación en la primera semana y, por primera vez en años, duermo toda la noche. ¡Mis niveles de energía son de cuando tenía 30!',
              result: '-6kg y adiós neblina mental en 30 días.'
            }, {
              name: 'Linda R.',
              location: 'Austin, TX',
              quote: 'Odiaba ir al gimnasio porque me sentía pesada y me dolían las rodillas. Las rutinas de 15 minutos de este plan son mágicas. Mis brazos están tonificados otra vez y ya no tengo esa ansiedad por el azúcar a las 4 de la tarde. Es el mejor dinero que he invertido en mí.',
              result: '2 tallas menos y vitalidad recuperada.'
            }, {
              name: 'Karen G.',
              location: 'San Diego, CA',
              quote: 'Era muy escéptica porque lo había intentado todo, desde Keto hasta ayunos extremos que solo me daban ansiedad. Este enfoque es tan lógico y fácil de seguir que no parece una dieta. Por fin reconozco a la mujer que me devuelve la mirada en el espejo.',
              result: 'Control total de antojos y cintura definida.'
            }].map((testimonial, index) => <FadeIn key={index} delay={0.2 * (index + 1)} data-id="element-75">
                  <div className="bg-brand-cream p-8 rounded-2xl h-full flex flex-col relative border border-brand-gold/10 hover:shadow-soft transition-shadow" data-id="element-76">
                    <div className="flex text-brand-gold mb-4" data-id="element-77">
                      {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-78" />)}
                    </div>
                    <p className="text-brand-gray italic mb-6 flex-grow" data-id="element-79">
                      "{testimonial.quote}"
                    </p>
                    <div className="pt-6 border-t border-brand-gold/20 mt-auto" data-id="element-80">
                      <p className="font-bold text-brand-dark" data-id="element-81">
                        {testimonial.name}
                      </p>
                      <p className="text-sm text-brand-gray mb-2" data-id="element-82">
                        {testimonial.location}
                      </p>
                      <div className="bg-brand-burgundy/10 text-brand-burgundy text-sm font-medium px-3 py-2 rounded-lg inline-block" data-id="element-83">
                        Resultado: {testimonial.result}
                      </div>
                    </div>
                  </div>
                </FadeIn>)}
            </div>
          </div>
        </section>

        {/* 7. Offer Presentation */}
        <section id="offer" className="px-4 py-24 bg-brand-dark text-white" data-id="element-84">
          <div className="max-w-5xl mx-auto" data-id="element-85">
            <FadeIn data-id="element-86">
              <h2 className="text-3xl md:text-5xl font-serif font-bold text-center mb-4 text-brand-goldLight" data-id="element-87">
                Presentamos: El Sistema Completo
              </h2>
              <p className="text-xl text-center text-brand-gold mb-16 font-serif italic" data-id="element-88">
                "Metabolic Reset 40+"
              </p>
            </FadeIn>

            <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-89">
              <FadeIn delay={0.2} data-id="element-90">
                <img src="https://cdn.magicpatterns.com/uploads/82ic4Uu3HvT5jGjwwggAbZ/Mocke_up.png" alt="Metabolic Reset 40+ Bundle" className="w-full h-auto rounded-2xl shadow-2xl shadow-brand-gold/20" data-id="element-91" />
              </FadeIn>

              <div className="space-y-6" data-id="element-92">
                {[{
                icon: BookOpen,
                title: 'El Manual Maestro (60+ pág)',
                desc: 'Tu nueva biblia biográfica para entender y dominar tus hormonas.',
                value: '$67'
              }, {
                icon: Calendar,
                title: 'Plan de Acción de 30 Días',
                desc: 'El mapa exacto de qué comer y qué hacer para no tener que pensar.',
                value: '$37'
              }, {
                icon: Utensils,
                title: 'Guía Restaurante y Vida Social',
                desc: 'Disfruta de una copa de vino y una cena fuera sin arruinar tu progreso.',
                value: '$27'
              }, {
                icon: PlayCircle,
                title: 'Método Fuerza en 15',
                desc: 'Video-demos de ejercicios que puedes hacer en pijama en tu sala.',
                value: '$27'
              }, {
                icon: ShoppingCart,
                title: 'Checklist del Súper Anti-Inflamatorio',
                desc: 'Entra y sal del súper con lo que tu cuerpo ama en 10 minutos.',
                value: '$17'
              }].map((item, index) => <FadeIn key={index} delay={0.3 + index * 0.1} data-id="element-93">
                    <div className="flex items-start gap-4 bg-white/5 p-4 rounded-xl border border-white/10 hover:bg-white/10 transition-colors" data-id="element-94">
                      <div className="bg-brand-gold/20 p-3 rounded-lg text-brand-gold shrink-0" data-id="element-95">
                        <item.icon className="w-6 h-6" data-id="element-96" />
                      </div>
                      <div data-id="element-97">
                        <h3 className="font-bold text-lg text-brand-goldLight flex items-center justify-between" data-id="element-98">
                          {item.title}
                          <span className="text-sm font-normal text-white/50 ml-2" data-id="element-99">
                            (Valor: {item.value})
                          </span>
                        </h3>
                        <p className="text-white/70 mt-1" data-id="element-100">{item.desc}</p>
                      </div>
                    </div>
                  </FadeIn>)}
              </div>
            </div>
          </div>
        </section>

        {/* 8. Pricing Section */}
        <section id="offer" className="px-4 py-20 bg-brand-goldLight/30" data-id="element-101">
          <div className="max-w-3xl mx-auto text-center" data-id="element-102">
            <FadeIn data-id="element-103">
              <p className="text-xl text-brand-gray mb-6" data-id="element-104">
                Si compraras cada pieza por separado, pagarías{' '}
                <span className="line-through decoration-red-500 decoration-2 font-bold" data-id="element-105">
                  $175
                </span>
                . Y valdría cada centavo por recuperar tu salud.
              </p>
              <p id="offer-intro" className="text-2xl font-serif text-brand-dark mb-10" data-id="element-106">
                Pero porque quiero que más mujeres como tú prueben este método y
                compartan su experiencia...
              </p>

              <div id="price-card" className="bg-white p-8 md:p-12 rounded-3xl shadow-xl border-2 border-brand-gold relative overflow-hidden mb-10" data-id="element-107">
                <div className="absolute top-0 right-0 bg-red-600 text-white text-xs font-bold px-4 py-1 rounded-bl-lg uppercase tracking-wider" data-id="element-108">
                  Oferta Limitada
                </div>
                <p className="text-lg text-brand-gray mb-1" data-id="element-109">
                  No vas a pagar $175.
                </p>
                <p className="text-lg text-brand-gray mb-4" data-id="element-110">Ni siquiera $48.</p>
                <p className="text-xl font-medium mb-2" data-id="element-111">
                  Llévate el Bundle Completo hoy por solo
                </p>
                <div className="text-7xl md:text-8xl font-bold text-brand-burgundy font-serif mb-6" data-id="element-112">
                  $12.37
                </div>
                <p className="text-sm text-brand-gray italic mb-4" data-id="element-113">
                  (Este precio especial está disponible para un grupo limitado
                  de lectoras adicionales — cuando se complete el cupo, el
                  precio regresa a $48)
                </p>
                <p className="text-base text-brand-dark font-medium" data-id="element-114">
                  Eso es menos de lo que cuesta un lunch. Sin excusas. Sin
                  riesgo.
                </p>
              </div>

              <div id="cta-button" data-id="element-115">
                <CTAButton href="https://innershiftstore.com/cart/51866486538522:1" onClick={handleAddToCart} data-id="element-116">
                  SÍ, ME SUMO AL MÉTODO $12.37
                </CTAButton>
              </div>
              <div className="flex items-center justify-center gap-2 text-sm text-brand-gray mt-4" data-id="element-117">
                <span data-id="element-118">
                  🔒 Pago seguro · Acceso inmediato · Garantía 30 días
                </span>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* 9. FAQ Section */}
        <section className="px-4 py-24 bg-white" data-id="element-119">
          <div className="max-w-3xl mx-auto" data-id="element-120">
            <FadeIn data-id="element-121">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-center mb-16" data-id="element-122">
                Preguntas Frecuentes
              </h2>
            </FadeIn>

            <div className="space-y-6" data-id="element-123">
              {[{
              q: '¿Esto requiere que cocine cosas raras o caras?',
              a: 'No. Usamos comida real que encuentras en cualquier tienda. Las recetas son de máximo 3-5 ingredientes. Somos pro-simplicidad.'
            }, {
              q: 'Ya no tengo mi ciclo, ¿todavía funciona para mí?',
              a: 'Absolutamente. Ya sea que estés en perimenopausia, menopausia o post-menopausia, el enfoque en la insulina y el cortisol es universal para tu biología actual.'
            }, {
              q: 'No tengo tiempo para hacer ejercicio...',
              a: 'Por eso nuestras rutinas son de 15 minutos. Si tienes tiempo para ver 3 Reels en Instagram, tienes tiempo para reprogramar tu metabolismo.'
            }, {
              q: '¿Tengo que dejar los carbohidratos o el café?',
              a: 'No. Aprendemos a usarlos de forma inteligente. No quitamos, optimizamos.'
            }, {
              q: '¿Es esto un pago único?',
              a: 'Sí. Un solo pago y tienes acceso de por vida a todo el material y sus actualizaciones.'
            }, {
              q: '¿Qué pasa si no me funciona?',
              a: 'Tienes mi garantía de 30 días. Si no sientes más energía y menos inflamación, te devuelvo cada centavo. Sin preguntas.'
            }, {
              q: '¿Recibiré algo por correo físico?',
              a: 'Es un producto 100% digital. Recibes acceso inmediato en tu email para empezar hace 5 minutos.'
            }].map((faq, index) => <FadeIn key={index} delay={0.1 * index} data-id="element-124">
                  <div className="bg-brand-cream rounded-xl p-6 border border-brand-gold/10" data-id="element-125">
                    <h3 className="font-bold text-lg text-brand-dark mb-3 flex items-start gap-3" data-id="element-126">
                      <div className="bg-brand-gold/20 rounded-full p-1 mt-0.5 shrink-0" data-id="element-127">
                        <ChevronDown className="w-4 h-4 text-brand-burgundy rotate-[-90deg]" data-id="element-128" />
                      </div>
                      {faq.q}
                    </h3>
                    <div className="flex items-start gap-3 pl-1" data-id="element-129">
                      <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" data-id="element-130" />
                      <p className="text-brand-gray" data-id="element-131">{faq.a}</p>
                    </div>
                  </div>
                </FadeIn>)}
            </div>
          </div>
        </section>

        {/* 10. Guarantee Section */}
        <section className="px-4 py-20 bg-brand-burgundy text-white" data-id="element-132">
          <div className="max-w-4xl mx-auto" data-id="element-133">
            <FadeIn data-id="element-134">
              <div className="border-2 border-brand-gold/30 rounded-3xl p-8 md:p-12 text-center bg-black/10 backdrop-blur-sm relative" data-id="element-135">
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-brand-gold text-brand-dark p-4 rounded-full shadow-lg" data-id="element-136">
                  <ShieldCheck className="w-8 h-8" data-id="element-137" />
                </div>
                <h2 className="text-2xl md:text-3xl font-serif font-bold mb-6 mt-4 text-brand-goldLight" data-id="element-138">
                  La Garantía de "Cero Riesgo, Máxima Confianza"
                </h2>
                <p className="text-lg md:text-xl leading-relaxed text-white/90 font-light" data-id="element-139">
                  Pruébalo por 30 días completos. Si al terminar el mes no te
                  sientes más ligera, con la mente más clara y con tu ropa
                  quedándote mejor, envíanos un email. Te devolvemos tu dinero y
                  te quedas con el material gratis por haberlo intentado.{' '}
                  <strong className="text-brand-goldLight font-medium" data-id="element-140">
                    El riesgo es todo mío.
                  </strong>
                </p>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* 11. Final CTA */}
        <section className="px-4 py-24 bg-brand-cream" data-id="element-141">
          <div className="max-w-4xl mx-auto text-center" data-id="element-142">
            <FadeIn data-id="element-143">
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-12" data-id="element-144">
                Tienes dos caminos frente a ti:
              </h2>

              <div className="grid md:grid-cols-2 gap-8 mb-16 text-left" data-id="element-145">
                <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm opacity-70" data-id="element-146">
                  <h3 className="text-xl font-bold text-gray-500 mb-4 flex items-center gap-2" data-id="element-147">
                    <span className="bg-gray-200 text-gray-600 w-8 h-8 rounded-full flex items-center justify-center text-sm" data-id="element-148">
                      A
                    </span>
                    Camino A: No hacer nada
                  </h3>
                  <p className="text-gray-500" data-id="element-149">
                    Seguir con las dietas de hambre, sintiéndote agotada y
                    viendo cómo cada año es más difícil reconocer tu cuerpo en
                    el espejo.
                  </p>
                </div>

                <div className="bg-white p-8 rounded-2xl border-2 border-brand-gold shadow-soft relative transform md:-translate-y-4" data-id="element-150">
                  <div className="absolute -top-3 -right-3 bg-brand-gold text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide" data-id="element-151">
                    Recomendado
                  </div>
                  <h3 className="text-xl font-bold text-brand-burgundy mb-4 flex items-center gap-2" data-id="element-152">
                    <span className="bg-brand-burgundy text-white w-8 h-8 rounded-full flex items-center justify-center text-sm" data-id="element-153">
                      B
                    </span>
                    Camino B: Probar el Reset
                  </h3>
                  <p className="text-brand-dark font-medium" data-id="element-154">
                    Darle a tu cuerpo lo que realmente necesita y despertar en
                    30 días sintiéndote fuerte, desinflamada y con una energía
                    que no sentías desde hace una década.
                  </p>
                </div>
              </div>

              <div className="space-y-6" data-id="element-155">
                <p className="text-xl font-serif italic text-brand-gray" data-id="element-156">
                  La decisión es tuya. Pero el precio de $12.37 no durará para
                  siempre.
                </p>
                <CTAButton href="https://innershiftstore.com/cart/51866486538522:1" onClick={handleAddToCart} data-id="element-157">
                  QUIERO MI ACCESO AHORA — SOLO $12.37
                </CTAButton>
                <div className="flex items-center justify-center gap-2 text-sm text-brand-gray mt-4" data-id="element-158">
                  <span data-id="element-159">
                    🔒 Pago seguro · Acceso inmediato · Garantía 30 días
                  </span>
                </div>
              </div>
            </FadeIn>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-brand-dark text-white/50 py-12 text-center text-sm" data-id="element-160">
        <div className="max-w-4xl mx-auto px-4" data-id="element-161">
          <p className="mb-4" data-id="element-162">
            © {new Date().getFullYear()} Metabolic Reset 40+. Todos los
            derechos reservados.
          </p>

          <div className="flex justify-center gap-6 mb-6" data-id="element-163">
            <button onClick={() => setIsPrivacyOpen(true)} className="hover:text-white transition-colors underline decoration-white/30 underline-offset-4" data-id="element-164">
              Política de Privacidad
            </button>
            <button onClick={() => setIsTermsOpen(true)} className="hover:text-white transition-colors underline decoration-white/30 underline-offset-4" data-id="element-165">
              Términos y Condiciones
            </button>
          </div>

          <p className="text-xs max-w-2xl mx-auto opacity-70" data-id="element-166">
            Este producto no sustituye el consejo médico profesional. Los
            resultados pueden variar según el individuo. La información
            proporcionada es solo para fines educativos e informativos.
          </p>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      {isPrivacyOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-id="element-167">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col shadow-2xl" data-id="element-168">
            <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-169">
              <h2 className="text-2xl font-serif font-bold text-brand-burgundy" data-id="element-170">
                Política de Privacidad
              </h2>
              <button onClick={() => setIsPrivacyOpen(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-171">
                <X className="w-5 h-5" data-id="element-172" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto text-brand-dark/80 space-y-4 text-sm leading-relaxed" data-id="element-173">
              <p data-id="element-174">
                <strong data-id="element-175">Última actualización:</strong>{' '}
                {new Date().toLocaleDateString()}
              </p>

              <h3 className="text-lg font-bold text-brand-dark mt-6 mb-2" data-id="element-176">
                1. Información que recopilamos
              </h3>
              <p data-id="element-177">
                Recopilamos información que usted nos proporciona directamente
                cuando realiza una compra, se suscribe a nuestro boletín o se
                comunica con nosotros. Esto puede incluir su nombre, dirección
                de correo electrónico y detalles de pago (procesados de forma
                segura a través de nuestros proveedores de pago).
              </p>

              <h3 className="text-lg font-bold text-brand-dark mt-6 mb-2" data-id="element-178">
                2. Uso de la información
              </h3>
              <p data-id="element-179">
                Utilizamos la información recopilada para procesar sus
                transacciones, enviarle el producto digital adquirido,
                proporcionarle soporte al cliente y enviarle actualizaciones
                relevantes sobre nuestros productos y servicios.
              </p>

              <h3 className="text-lg font-bold text-brand-dark mt-6 mb-2" data-id="element-180">
                3. Protección de datos
              </h3>
              <p data-id="element-181">
                Implementamos medidas de seguridad estándar de la industria para
                proteger su información personal. No vendemos, intercambiamos ni
                transferimos su información personal a terceros sin su
                consentimiento, excepto a los proveedores de servicios de
                confianza que nos ayudan a operar nuestro sitio web y llevar a
                cabo nuestro negocio.
              </p>

              <h3 className="text-lg font-bold text-brand-dark mt-6 mb-2" data-id="element-182">
                4. Cookies
              </h3>
              <p data-id="element-183">
                Utilizamos cookies para mejorar su experiencia en nuestro sitio
                web, analizar el tráfico y personalizar el contenido. Puede
                configurar su navegador para que rechace las cookies, pero esto
                puede limitar algunas funcionalidades del sitio.
              </p>
            </div>
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end" data-id="element-184">
              <button onClick={() => setIsPrivacyOpen(false)} className="px-6 py-2 bg-brand-burgundy text-white rounded-full font-medium hover:bg-brand-burgundyDark transition-colors" data-id="element-185">
                Cerrar
              </button>
            </div>
          </motion.div>
        </div>}

      {/* Terms and Conditions Modal */}
      {isTermsOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-id="element-186">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col shadow-2xl" data-id="element-187">
            <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-188">
              <h2 className="text-2xl font-serif font-bold text-brand-burgundy" data-id="element-189">
                Términos y Condiciones
              </h2>
              <button onClick={() => setIsTermsOpen(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-190">
                <X className="w-5 h-5" data-id="element-191" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto text-brand-dark/80 space-y-4 text-sm leading-relaxed" data-id="element-192">
              <p data-id="element-193">
                <strong data-id="element-194">Última actualización:</strong>{' '}
                {new Date().toLocaleDateString()}
              </p>

              <h3 className="text-lg font-bold text-brand-dark mt-6 mb-2" data-id="element-195">
                1. Aceptación de los términos
              </h3>
              <p data-id="element-196">
                Al acceder y utilizar este sitio web y comprar nuestros
                productos digitales, usted acepta estar sujeto a estos Términos
                y Condiciones. Si no está de acuerdo con alguna parte de estos
                términos, no debe utilizar nuestro sitio ni comprar nuestros
                productos.
              </p>

              <h3 className="text-lg font-bold text-brand-dark mt-6 mb-2" data-id="element-197">
                2. Productos Digitales
              </h3>
              <p data-id="element-198">
                El "Metabolic Reset 40+ Bundle" es un producto digital. Al
                realizar la compra, recibirá acceso inmediato o enlaces de
                descarga al contenido. No se enviará ningún producto físico.
              </p>

              <h3 className="text-lg font-bold text-brand-dark mt-6 mb-2" data-id="element-199">
                3. Propiedad Intelectual
              </h3>
              <p data-id="element-200">
                Todo el contenido incluido en nuestros productos digitales
                (textos, gráficos, logotipos, imágenes, audios, videos) es
                propiedad exclusiva de Metabolic Reset 40+ y está protegido por
                las leyes de derechos de autor. La compra le otorga una licencia
                personal, no exclusiva y no transferible para utilizar el
                material. Queda estrictamente prohibida la reventa, distribución
                o modificación del contenido.
              </p>

              <h3 className="text-lg font-bold text-brand-dark mt-6 mb-2" data-id="element-201">
                4. Descargo de Responsabilidad Médica
              </h3>
              <p data-id="element-202">
                La información proporcionada en nuestros productos tiene fines
                puramente educativos e informativos. No constituye consejo
                médico, diagnóstico ni tratamiento. Siempre consulte a un
                profesional de la salud cualificado antes de comenzar cualquier
                dieta, programa de ejercicios o hacer cambios significativos en
                su estilo de vida.
              </p>

              <h3 className="text-lg font-bold text-brand-dark mt-6 mb-2" data-id="element-203">
                5. Política de Reembolso
              </h3>
              <p data-id="element-204">
                Ofrecemos una garantía de devolución de dinero de 30 días. Si no
                está satisfecho con el producto, puede solicitar un reembolso
                completo dentro de los 30 días posteriores a la fecha de compra.
              </p>
            </div>
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end" data-id="element-205">
              <button onClick={() => setIsTermsOpen(false)} className="px-6 py-2 bg-brand-burgundy text-white rounded-full font-medium hover:bg-brand-burgundyDark transition-colors" data-id="element-206">
                Cerrar
              </button>
            </div>
          </motion.div>
        </div>}
    </div>;
}